
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import aluminumDoorsImg from "@/assets/aluminum-doors.jpg";
import aluminumWindowsImg from "@/assets/aluminum-windows.jpg";
import aluminumAwningsImg from "@/assets/aluminum-awnings.jpg";
import aluminumKitchenImg from "@/assets/aluminum-kitchen.jpg";
import aluminumGlassFacadeImg from "@/assets/aluminum-glass-facade.jpg";

const Aluminum = () => {
  const { language, isRTL } = useLanguage();

  const productSlugs = ["doors", "windows", "shades", "kitchens", "facades"];

  const content = {
    ar: {
      title: "منتجات الألمنيوم",
      subtitle: "نقدم تشكيلة واسعة من منتجات الألمنيوم عالية الجودة بتصاميم عصرية وحديثة تلبي احتياجات عملائنا وتتماشى مع الظروف المناخية المختلفة. منتجاتنا تتميز بالعزل الحراري والمائي الممتاز، ومقاومة الغبار والضوضاء، وسهولة الصيانة والتنظيف، ومتانة تدوم لسنوات طويلة.",
      viewDetails: "عرض التفاصيل",
      products: [
        { title: "أبواب ألمنيوم", description: "تصميمات أبواب فخمة واعتيادية بمواصفات عالية الجودة تجمع بين الأناقة والمتانة. نقدم حلولًا متكاملة للمنازل والفلل والشركات والمباني التجارية." },
        { title: "نوافذ ألمنيوم", description: "شبابيك ألمنيوم حديثة بجودة عالية تلبي الاحتياجات وتتماشى مع المناخ، مع عزل حراري ومائي ممتاز ومقاومة للغبار والضوضاء." },
        { title: "مظلات وسواتر", description: "كافة أنواع المظلات والسواتر وكاسرات الشمس والرياح بتصاميم عصرية. تحمي من أشعة الشمس والأمطار والرياح مع الحفاظ على الجمال المعماري." },
        { title: "مطابخ ألمنيوم", description: "مطابخ عصرية وحديثة مصنوعة من الألمنيوم عالي الجودة بتصميمات مميزة. تجمع بين الجمال والمتانة وسهولة التنظيف ومقاومة الحرارة والرطوبة." },
        { title: "واجهات زجاجية", description: "واجهات زجاجية للمباني والأبراج بتصاميم مبتكرة وجودة عالية. تضيف لمسة عصرية وأنيقة للمباني مع توفير إضاءة طبيعية وإطلالات رائعة." }
      ]
    },
    en: {
      title: "Aluminum Products",
      subtitle: "We offer a wide range of high-quality aluminum products with modern designs that meet our customers' needs and adapt to different climatic conditions. Our products feature excellent thermal and water insulation, dust and noise resistance, easy maintenance and cleaning, and durability that lasts for years.",
      viewDetails: "View Details",
      products: [
        { title: "Aluminum Doors", description: "Luxurious and standard door designs with high-quality specifications combining elegance and durability. We provide complete solutions for homes, villas, companies, and commercial buildings." },
        { title: "Aluminum Windows", description: "Modern high-quality aluminum windows that meet needs and adapt to the climate, with excellent thermal and water insulation and resistance to dust and noise." },
        { title: "Shades & Screens", description: "All types of shades, screens, and sun/wind breakers with modern designs. Protection from sunlight, rain, and wind while maintaining architectural beauty." },
        { title: "Aluminum Kitchens", description: "Modern kitchens made from high-quality aluminum with distinctive designs. Combining beauty, durability, easy cleaning, and heat and moisture resistance." },
        { title: "Glass Facades", description: "Glass facades for buildings and towers with innovative designs and high quality. Adding a modern and elegant touch to buildings with natural lighting and great views." }
      ]
    }
  };

  const t = content[language];
  const images = [aluminumDoorsImg, aluminumWindowsImg, aluminumAwningsImg, aluminumKitchenImg, aluminumGlassFacadeImg];

  return (
    <div className="min-h-screen bg-gray-50 py-12" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-kufi font-bold text-center mb-6 text-primary">{t.title}</h1>
        <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-12">{t.subtitle}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.products.map((product, index) => (
            <motion.div key={product.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                <img src={images[index]} alt={product.title} className="w-full h-48 object-cover" />
                <CardContent className="p-6 flex-grow flex flex-col">
                  <h3 className="text-xl font-kufi font-bold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-4 flex-grow">{product.description}</p>
                  <Link to={`/aluminum/${productSlugs[index]}`}>
                    <Button className="bg-primary hover:bg-primary/90 w-full">{t.viewDetails}</Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Aluminum;
